import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, ArrowLeft, Lock, Eye, EyeOff, ToggleLeft, ToggleRight, Clock, DollarSign, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import Navbar from "@/components/layout/Navbar";

const PlusCardPage = () => {
  const [showNumber, setShowNumber] = useState(false);
  const [cardActive, setCardActive] = useState(true);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="plus-card-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Work Hub
        </Link>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-bold text-emerald-950 flex items-center gap-2 mb-6">
            <CreditCard className="w-6 h-6 text-emerald-600" /> Stack Plus Card
          </h1>

          {/* Virtual Card */}
          <div className="bg-gradient-to-br from-emerald-800 via-emerald-900 to-emerald-950 rounded-3xl p-6 mb-6 text-white relative overflow-hidden" data-testid="virtual-card">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-8">
                <p className="text-emerald-300 text-sm font-medium">STACK PLUS</p>
                <Badge className={cardActive ? "bg-green-500/20 text-green-300 border-green-500/30" : "bg-red-500/20 text-red-300 border-red-500/30"}>
                  {cardActive ? "Active" : "Blocked"}
                </Badge>
              </div>
              <div className="mb-6">
                <p className="text-2xl font-mono tracking-wider">
                  {showNumber ? "4532 8721 0094 3367" : "**** **** **** 3367"}
                </p>
                <button onClick={() => setShowNumber(!showNumber)} className="text-emerald-300 text-xs mt-1 flex items-center gap-1 hover:text-white transition-colors" data-testid="toggle-number">
                  {showNumber ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showNumber ? "Hide" : "Show"} number
                </button>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs text-emerald-400">BALANCE</p>
                  <p className="text-xl font-bold" data-testid="card-balance">$0.00</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-emerald-400">EXPIRES</p>
                  <p className="font-mono">12/28</p>
                </div>
              </div>
            </div>
          </div>

          {/* Card Controls */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-6" data-testid="card-controls">
            <h3 className="font-semibold text-emerald-950 mb-4">Card Controls</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                    {cardActive ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4 text-stone-400" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-emerald-950">Card Status</p>
                    <p className="text-xs text-stone-500">{cardActive ? "Card is active and can be used" : "Card is blocked"}</p>
                  </div>
                </div>
                <Switch checked={cardActive} onCheckedChange={setCardActive} data-testid="card-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-emerald-950">Online Transactions</p>
                    <p className="text-xs text-stone-500">Allow online purchases</p>
                  </div>
                </div>
                <Switch defaultChecked={true} />
              </div>
            </div>
          </div>

          {/* Transaction History */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5" data-testid="card-transactions">
            <h3 className="font-semibold text-emerald-950 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-stone-400" /> Transaction History
            </h3>
            <div className="text-center py-8">
              <DollarSign className="w-10 h-10 text-stone-200 mx-auto mb-2" />
              <p className="text-stone-500 text-sm">No transactions yet</p>
              <p className="text-stone-400 text-xs mt-1">Card transactions will appear here</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PlusCardPage;
